import { Nav } from "@/components/navigation/Navbar";
import * as Text from "@/components/text";
import Link from "next/link";
import { useRef } from "react";
import apiClient from "@/utils/apiClient";
import { TitlesSlider } from "@/components/blog/FeatureTitlesContainer";
// Import Swiper styles
import "swiper/css";
import {
  XIcon,
  CheckIcon,
  ArrowUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/solid";
import { VerticalCard, HorizontalCard } from "@/components/publications/Cards";
import PosterCard from "@/components/blog/BlogPoster";
import SearchBarBlog from "@/components/blog/SearchBarBlog";
import Crumbs from "@/components/navigation/Crumbs";
import { usePrevious } from "@uidotdev/usehooks";

import Filter, { MobileFilter } from "@/components/forms/Filter";
import ScrollHiderWrapper from "@/components/animation/ScrollHiderWrapper";
import React,{ useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useGetThemes } from "@/actions/useMovies";
import { useCategories } from "@/actions/searchActions";
import { CardSlider } from "@/components/slider";
import ReactPaginate from "react-paginate";
import useWindowSize from "@/hooks/useWindowSize";
import breakpoints from "@/constants/breakpoinst";
import { Listbox } from "@headlessui/react";
import Button from "@/components/actions/Button";
import classNames from "classnames";
import { localeTexts} from "@/utils/locale";

export default function Blog({
  mainContent,
  resenasContent = [],
  noticiasContent,
  searchArticles,
  locale="es-MX",
}) {
  const topRef = useRef(null);
  const router = useRouter();
  const [selectedThemes, setSelectedThemes] = useState(
    router?.query?.temas?.split(",") || []
  );
  const [selectedCategories, setSelectedCategories] = useState(
    router?.query?.categorias?.split(",") || []
  );
  const previousCategories = usePrevious(selectedCategories);
  const previousThemes = usePrevious(selectedThemes);
  const [themeFilters, setThemeFilters] = useState([]);
  const [categoriesFilters, setCategoriesFilters] = useState([]);
  const { data: themes, error, isLoading } = useGetThemes();
  const {
    data: categoriesData,
    error: categoriesError,
    isLoading: categoriesLoading,
  } = useCategories();
  const { width } = useWindowSize();
  const [mainContentData, setMainContentData] = React.useState({...mainContent});
	
  useEffect(() => {
    if (selectedThemes.length > 0) {
      const themes = selectedThemes.join(",");
      router.push({
        pathname: "/blog",
        query: {
          temas: themes,
        },
      });
    } else {
      if (previousThemes?.length > 0) {
        router.push({
          pathname: "/blog",
        });
      }
    }
  }, [selectedThemes]);

  useEffect(() => {
    if (selectedCategories.length > 0) {
      const categories = selectedCategories.join(",");
      const { query } = router;
      query["categorias"] = categories;
      router.push({
        pathname: "/blog",
        query,
      });
    } else {
      if (previousCategories?.length > 0) {
        router.push({
          pathname: "/blog",
        });
      }
    }
  }, [selectedCategories]);

  useEffect(() => {
    if (themes?.data.length > 0) {
      setThemeFilters(
        themes?.data?.map((theme) => ({
          name: theme.attributes.name,
          value: theme.attributes.slug,
        }))
      );
    }
  }, [themes]);

  useEffect(() => {
    if (categoriesData?.data.length > 0) {
      setCategoriesFilters(
        categoriesData?.data?.map((category) => ({
          name: category.attributes.name,
          value: category.attributes.slug,
        }))
      );
    }
  }, [categoriesData]);

    useEffect(() => {
    if (locale === "en" && mainContent.localizations?.data?.length > 0) {
    // Find the English localization
    const englishData = mainContent.localizations.data.find(
    (localization) => localization.attributes.locale === "en"
    );

    if (englishData) {
    setMainContentData({
        ...mainContent, // Preserve other attributes
        ...englishData.attributes, // Override with English attributes
    });
    }
    } else {
    // Reset to the default locale (original attributes)
    setMainContentData({ ...mainContent });
    }
  }, [locale, mainContent]);

  return (
    <section key="catalogo-index">
      <div ref={topRef}>
        <ScrollHiderWrapper className="bg-white" scrollYAmount={-80}>
          <div className="border-b-2">
            <div className="amb-container">
              <Nav className="grow bg-gray-100" theme="light" />
            </div>
          </div>
          <section className="sm:mx-4 md:mx-8 lg:mx-28 xl:mx-auto xl:max-w-[1208px]">
            <div className="flex flex-row gap-2 py-4 ">
              <Crumbs
                breadcrumbs={[
                  { href: "/", text: localeTexts[locale].inicio },
                  { href: "/blog", text: "Blog" },
                ]}
              />
            </div>
            <section className="flex flex-col pb-4 sm:mt-0 sm:flex-row sm:justify-between sm:gap-4 md:flex-row lg:py-4 ">
              <SearchBarBlog />
              <div className="hidden gap-2 lg:flex">
                <Filter
                  name={localeTexts[locale].blog.theme}
                  align="right"
                  filters={themeFilters}
                  onChange={setSelectedThemes}
                  selected={selectedThemes}
                />
                <Filter
                  name={localeTexts[locale].blog.publicationType}
                  align="right"
                  filters={categoriesFilters}
                  onChange={setSelectedCategories}
                  selected={selectedCategories}
                />
              </div>
              <div className="lg:hidden">
                <MobileFilter
                  themeFilters={themeFilters}
                  onThemeChange={setSelectedThemes}
                  selectedThemes={selectedThemes}
                  categoriesFilters={categoriesFilters}
                  onCategoryChange={setSelectedCategories}
                  selectedCategories={selectedCategories}
                />
              </div>
            </section>

            <div className="flex flex-wrap gap-2 py-3 md:py-5">
              {selectedThemes.map((t, i) => (
                <div
                  key={i}
                  className="flex items-center rounded-full bg-neutral-100 px-3"
                >
                  <Text.Body
                    as="p"
                    className="w-max text-neutral-900"
                    key={t}
                    size="lg"
                  >
                    &nbsp;
                    {themeFilters.find((theme) => theme.value === t)?.name}
                  </Text.Body>
                  &nbsp;
                  <XIcon
                    className="ml-2 flex h-4 w-4 cursor-pointer rounded-full hover:bg-black hover:text-white"
                    onClick={() => {
                      setSelectedThemes(
                        selectedThemes.filter((theme) => theme !== t)
                      );
                    }}
                  ></XIcon>
                </div>
              ))}

              {selectedCategories.map((t, i) => (
                <div
                  key={i}
                  className="flex items-center rounded-full bg-neutral-100 px-3"
                >
                  <Text.Body
                    as="p"
                    className="w-max text-neutral-900"
                    key={t}
                    size="lg"
                  >
                    &nbsp;
                    {
                      categoriesFilters.find((category) => category.value === t)
                        ?.name
                    }
                  </Text.Body>
                  &nbsp;
                  <XIcon
                    className="ml-2 flex h-4 w-4 cursor-pointer rounded-full hover:bg-black hover:text-white"
                    onClick={() => {
                      setSelectedCategories(
                        selectedCategories.filter((category) => category !== t)
                      );
                    }}
                  ></XIcon>
                </div>
              ))}

              {(selectedCategories.length !== 0 ||
                selectedThemes.length !== 0) && (
                  <Text.Link
                    className="font-extrabold "
                    onClick={() => {
                      setSelectedCategories([]);
                      setSelectedThemes([]);
                    }}
                    label={localeTexts[locale].blog.clearFilters}
                  ></Text.Link>
                )}
            </div>
          </section>
        </ScrollHiderWrapper>
      </div>

      {!searchArticles ? (
        <>
          <div className="mt-44 pb-4 lg:mt-60">
            <TitlesSlider titles={mainContentData.entradas.data} />
          </div>
          <section className="space-y-3 sm:ml-4 sm:mt-12 md:ml-6 lg:mx-[116px] xl:mx-auto xl:max-w-[1208px]">
            <div className="flex items-center">
              <span className="hidden text-[1.75rem] font-bold leading-[2rem] tracking-[-0.008rem] md:block">
                {localeTexts[locale].blog.news}
              </span>
              <div className="flex-grow"></div>
              <Link href={`/blog?categorias=noticias`}>
                <Text.Link
                  className="text-neutral-600 hover:text-neutral-900"
                  label={localeTexts[locale].blog.viewAll}
                ></Text.Link>
              </Link>
            </div>
            <CardSlider
              slides={noticiasContent
                .sort(
                  (a, b) =>
                    new Date(b.attributes.publishedAt) -
                    new Date(a.attributes.publishedAt)
                )
                .map((post) => (
                  <PosterCard
                    key={post.id}
                    grid_details={true}
                    theme="light"
                    slug={`/blog/${post.attributes.slug}`}
                    title={post.attributes.titulo}
                    date={new Date(post.attributes.publishedAt)}
                    tag={
                      post.attributes.categorias?.data[0]?.attributes?.name ||
                      localeTexts[locale].blog.news
                    }
                    image={
                      post.attributes.cover_image.data?.attributes?.formats
                        ?.medium?.url
                    }
                    metadata={{
                      date: new Date(post.attributes.publishedAt),
                      type: post.type === "post" ? "Noticia" : "",
                      length: "",
                    }}
                  />
                ))}
            />
          </section>
          <section className="space-y-3 sm:ml-4 sm:mt-12 md:ml-6 lg:mx-[116px] xl:mx-auto xl:max-w-[1208px]">
            <div className="flex items-center">
              <span className="hidden text-[1.75rem] font-bold leading-[2rem] tracking-[-0.008rem] md:block">
                {localeTexts[locale].blog.reviews}
              </span>
              <div className="flex-grow"></div>
              <Link href={`/blog?categorias=resena`}>
                <Text.Link
                  className="text-neutral-600 hover:text-neutral-900"
                  label={localeTexts[locale].blog.viewAll}
                ></Text.Link>
              </Link>
            </div>
            <CardSlider
              slides={resenasContent
                .sort(
                  (a, b) =>
                    new Date(b.attributes.publishedAt) -
                    new Date(a.attributes.publishedAt)
                )
                .map((post) => (
                  <PosterCard
                    key={post.id}
                    grid_details={true}
                    theme="light"
                    slug={`/blog/${post.attributes.slug}`}
                    title={post.attributes.titulo}
                    date={new Date(post.attributes.publishedAt)}
                    description={post.attributes.contenido}
                    tag={
                      post.attributes.categorias?.data[0]?.attributes?.name ||
                      localeTexts[locale].blog.reviews
                    }
                    image={
                      post.attributes.cover_image.data?.attributes?.formats
                        ?.medium?.url
                    }
                    metadata={{
                      date: new Date(post.attributes.publishedAt),
                      type: post.type === "post" ? "Noticia" : "",
                      length: "",
                    }}
                  />
                ))}
            />
          </section>
          {mainContentData.temas.data.map((theme) => (
            <section
              key={theme.id}
              className="space-y-3 sm:ml-4 sm:mt-12 md:ml-6 lg:mx-[116px] xl:mx-auto xl:max-w-[1208px]"
            >
              <div className="flex items-center">
                <Text.Headline className="hidden md:block">
                  {theme.attributes.name}
                </Text.Headline>
                <Text.Title className="md:hidden">
                  {theme.attributes.name}
                </Text.Title>
                <div className="flex-grow"></div>
                <Link href={`/blog?temas=${theme.attributes.slug}`}>
                  <Text.Link
                    className="text-neutral-600 hover:text-neutral-900"
                    label={localeTexts[locale].blog.viewAll}
                  ></Text.Link>
                </Link>
              </div>

              <CardSlider
                slides={theme.attributes.entradas.data
                  .sort(
                    (a, b) =>
                      new Date(b.attributes.publishedAt) -
                      new Date(a.attributes.publishedAt)
                  )
                  .map((post) => (
                    <PosterCard
                      key={post.id}
                      grid_details={true}
                      theme="light"
                      slug={`/blog/${post.attributes.slug}`}
                      title={post.attributes.titulo}
                      date={new Date(post.attributes.publishedAt)}
                      description={post.attributes.contenido}
                      tag={
                        post.attributes.categorias?.data[0]?.attributes?.name ||
                        localeTexts[locale].blog.note
                      }
                      image={
                        post.attributes.cover_image.data?.attributes?.formats
                          ?.medium?.url
                      }
                      metadata={{
                        date: new Date(post.attributes.publishedAt),
                        type: post.type === "post" ? "Noticia" : "",
                        length: "",
                      }}
                    />
                  ))}
              />
            </section>
          ))}

          <section className="flex justify-center sm:ml-4 sm:mt-12 md:ml-6 lg:mx-[116px] xl:mx-auto xl:max-w-[1208px]">
            <Link
              href={{
                pathname: "blog",
                query: {
                  todo: "true",
                },
              }}
            >
              <button className="border-b-2 font-semibold">
                {localeTexts[locale].blog.viewAllPublications}
              </button>
            </Link>
          </section>
        </>
      ) : (
        <div className="amb-container mt-44 space-y-5 pb-4 lg:mt-72">
          <div className="flex">
            <Text.Headline className="mb-4">
              {localeTexts[locale].blog.searchResults}
            </Text.Headline>
            <div className="grow"></div>
            <Listbox
              value={router?.query?.ordenar || "asc"}
              onChange={(value) => {
                router.push({
                  pathname: "/blog",
                  query: {
                    ...router.query,
                    ordenar: value,
                  },
                });
              }}
            >
              {({ open }) => (
                <div className="relative">
                  <Listbox.Button
                    as={Button}
                    color={open ? "white-on-black-no-hover" : "primary-light"}
                    label={
                      router?.query?.ordenar === "asc"
                        ? "Más antiguas"
                        : "Más recientes"
                    }
                    className="relative px-4 font-semibold "
                    trailingIcon={<ChevronDownIcon width={32} height={32} />}
                  ></Listbox.Button>
                  <Listbox.Options
                    className={classNames(
                      "right-0",
                      "top -10 absolute z-50  flex max-h-[400px] w-[254px]  flex-col overflow-y-auto border-[1px] border-black bg-white"
                    )}
                  >
                    <Listbox.Option
                      as="div"
                      className="flex py-2 sm:px-4"
                      value="asc"
                    >
                      {({ selected, active }) => (
                        <div className="flex gap-2">
                          <span
                            className={`${selected ? "font-medium" : "font-normal"
                              } block truncate`}
                          >
                            Más antiguas
                          </span>
                          {selected ? (
                            <span>
                              <CheckIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </span>
                          ) : null}
                        </div>
                      )}
                    </Listbox.Option>
                    <Listbox.Option
                      as="div"
                      className="flex py-2 sm:px-4"
                      value="desc"
                    >
                      {({ selected, active }) => (
                        <div className="flex gap-2">
                          <span
                            className={`${selected ? "font-medium" : "font-normal"
                              } block truncate`}
                          >
                            Más recientes
                          </span>
                          {selected ? (
                            <span>
                              <CheckIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </span>
                          ) : null}
                        </div>
                      )}
                    </Listbox.Option>
                  </Listbox.Options>
                </div>
              )}
            </Listbox>
          </div>
          <div>
            <Text.Body size="xl">
              Se encontraron {searchArticles?.meta?.pagination?.total}&nbsp;
              resultados para la búsqueda
            </Text.Body>
          </div>

          <div className="hidden gap-4 lg:grid lg:grid-cols-4">
            {searchArticles?.data?.map((article) => (
              <PosterCard
                key={article.id}
                grid_details={true}
                theme="light"
                slug={`/blog/${article.attributes.slug}`}
                title={article.attributes.titulo}
                date={new Date(article.attributes.publishedAt)}
                description={article.attributes.contenido}
                tag={
                  article.attributes.categorias?.data[0]?.attributes?.name ||
                  "Nota"
                }
                image={
                  article.attributes.cover_image.data?.attributes?.formats
                    ?.medium?.url
                }
                metadata={{
                  date: new Date(article.attributes.publishedAt),
                  type: article.type === "post" ? "Noticia" : "",
                  length: "",
                }}
              />
            ))}
          </div>
          <div className="grid-cols-2 gap-4 md:grid lg:hidden">
            {searchArticles?.data?.map((article) => (
              <HorizontalCard
                key={article.id}
                metadata={{
                  date: new Date(article.attributes?.publishedAt),
                  type: article.attributes?.categorias?.data[0]?.attributes
                    ?.name,
                }}
                link={`/blog/${article.attributes?.slug}`}
                title={article.attributes?.titulo}
                description={article.attributes?.contenido}
                image={article?.attributes?.cover_image?.data?.attributes?.url}
              />
            ))}
          </div>

          {searchArticles.meta.pagination.pageCount > 1 && (
            <ReactPaginate
              previousLabel={localeTexts[locale].blog.previous}
              nextLabel={localeTexts[locale].blog.next}
              breakLabel={"..."}
              breakClassName={"break-me"}
              onPageChange={(event) => {
                router.push({
                  pathname: "/blog",
                  query: {
                    ...router.query,
                    page: event.selected + 1,
                  },
                });
              }}
              marginPagesDisplayed={width > breakpoints.md ? 3 : 1}
              pageCount={searchArticles.meta.pagination.pageCount}
              containerClassName={
                "flex justify-center gap-5 mt-10 items-center"
              }
              pageClassName={
                "text-neutral-900 bg-neutral-50 hover:bg-neutral-100 w-12 h-12 flex items-center text-center"
              }
              activeClassName={"border-2 border-black"}
              pageLinkClassName={
                "w-full h-full flex items-center justify-center"
              }
              previousClassName={
                "text-neutral-900 bg-neutral-50 hover:bg-neutral-100 px-5 h-12 flex items-center text-center"
              }
              nextClassName={
                "text-neutral-50 bg-neutral-900 hover:bg-neutral-800 px-5 h-12 flex items-center text-center"
              }
            />
          )}

          <div className="text-center ">
            <div className="mt-10">
              <Text.Link
                onClick={() => {
                  topRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "end",
                    inline: "nearest",
                  });
                }}
                label={localeTexts[locale].blog.backToTop}
                trailingIcon={<ArrowUpIcon />}
                size="lg"
                className="m-auto text-center "
              >
                &nbsp;
              </Text.Link>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
export async function getServerSideProps(context) {
  const { locale } = context.query;
  try {
    const { query: q } = context;
    if (
      q?.buscar ||
      q?.temas ||
      q?.categorias ||
      q?.todo ||
      q?.page ||
      q?.ordenar
    ) {
      const qs = require("qs");
      const query = qs.stringify({
        populate: "*",
        localizations:"*",
        sort: {
          publishedAt: q?.ordenar || "desc",
        },
        filters: {
          $or: [
            q?.buscar && {
              titulo: { $containsi: q.buscar },
            },
            q?.temas && {
              temas: {
                slug: {
                  $in: q.temas.split(","),
                },
              },
            },
            q?.categorias && {
              categorias: {
                slug: {
                  $in: q.categorias.split(","),
                },
              },
            },
          ],
        },
        pagination: {
          page: q?.page || 1,
        },
      });
      const searchArticles = await apiClient.get(`/api/entradas?${query}`);

      return {
        props: {
          searchArticles: searchArticles.data,
        },
      };
    } else {
      const qs = require("qs");

      const mainQuery = qs.stringify(
        {
          populate: {
            localizations:"*",
            entradas: {
              populate: {
                cover_image: "*",
                titulo: "*",
                subtitulo: "*",
                autor: "*",
                contenido: "*",
                publishedAt: "*",
                rich_text: "*",
                slug: "*",
                peliculas: "*",
                categorias: "*",
              },
            },
            temas: {
              populate: {
                entradas: {
                  populate: {
                    cover_image: "*",
                  },
                },
              },
            },
          },
        },
        { encodeValuesOnly: true }
      );

      const { data: mainContent } = await apiClient.get(
        `/api/blog?${mainQuery}`
      );

      const resenasQuery = qs.stringify(
        {
          populate: {
            cover_image: "*",
          },
          sort: ["createdAt:desc"],
          filters: {
            entry_type: {
              $eq: "Reseñas",
            },
          },
        },
        { encodeValuesOnly: true }
      );

      const { data: resenasContent } = await apiClient.get(
        `/api/entradas?${resenasQuery}`
      );

      const noticiasQuery = qs.stringify(
        {
          populate: {
            cover_image: "*",
          },
          filters: {
            entry_type: {
              $eq: "Noticias",
            },
          },
        },
        { encodeValuesOnly: true }
      );

      const { data: noticiasContent } = await apiClient.get(
        `/api/entradas?${noticiasQuery}`
      );
      const data = [];
      data.attributes;
      return {
        props: {
          mainContent: mainContent.data.attributes,
          resenasContent: resenasContent.data ?? [],
          noticiasContent: noticiasContent.data,
          locale: locale || "es-MX",
        },
      };
    }
  } catch (error) {
    console.error(error);
    return {
      notFound: true,
    };
  }
}
